import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Column, EmptyState, SearchInput } from "@hightouch/htui";
import { useInfiniteQuery } from "react-query";
import {
  useAmpBoardTasksQuery,
  OrderBy,
  type AmpBoardTasksQuery,
  type AmpBoardTasksQueryVariables,
} from "src/graphql";
import { StatusIcon } from "src/pages/projects/components/status-icon";
import { taskCategory } from "src/pages/projects/utils";
import { useScrollFade } from "src/hooks/use-scroll-fade";
import { useCanvasReview } from "src/pages/projects/library/canvas/canvas-review-context";
import {
  ReviewPickerNote,
  ReviewPickerRow,
} from "src/pages/projects/library/canvas/canvas-review-rows";
import { moveFocusWithArrows } from "src/pages/projects/library/canvas/canvas-review-keys";
import type { ReviewFile } from "src/pages/projects/library/canvas/canvas-review";

const PAGE_SIZE = 30;

/** Finds a task to link inside the linked-tasks popover: a search row with its results below. Escape or a link closes it. */
export function CanvasReviewTaskPicker({
  file,
  onClose,
}: {
  file: ReviewFile;
  onClose: () => void;
}) {
  const [search, setSearch] = useState("");
  const [pendingId, setPendingId] = useState<string>();
  const review = useCanvasReview();
  const lastPageRequest = useRef<{ search: string; pageCount: number }>();
  const searchRef = useRef<HTMLInputElement>(null);
  const firstRowRef = useRef<HTMLButtonElement>(null);
  const scrollFade = useScrollFade();
  useEffect(() => {
    searchRef.current?.focus();
  }, []);
  const variables: AmpBoardTasksQueryVariables = {
    where: {
      title: { _ilike: `%${search.replace(/[\\%_]/g, "\\$&")}%` },
      deleted_at: { _is_null: true },
      archived_at: { _is_null: true },
      project: {
        deleted_at: { _is_null: true },
        archived_at: { _is_null: true },
      },
    },
    orderBy: [{ title: OrderBy.Asc }, { id: OrderBy.Asc }],
    limit: PAGE_SIZE,
    offset: 0,
  };
  const query = useInfiniteQuery<AmpBoardTasksQuery, Error>(
    [...useAmpBoardTasksQuery.getKey(variables), "infinite"],
    ({ pageParam = 0 }) =>
      useAmpBoardTasksQuery.fetcher({ ...variables, offset: pageParam })(),
    {
      getNextPageParam: (page, pages) =>
        page.amp_tasks.length === PAGE_SIZE
          ? pages.length * PAGE_SIZE
          : undefined,
    },
  );
  const linked = new Set(review?.tasks.get(file.id)?.map((task) => task.id));
  const tasks = useMemo(
    () => query.data?.pages.flatMap((page) => page.amp_tasks) ?? [],
    [query.data],
  );
  const firstLinkable = tasks.findIndex((task) => !linked.has(task.id));
  const isBusy = Boolean(review?.isBusy) || Boolean(pendingId);

  const link = async (task: (typeof tasks)[number]) => {
    if (!review || isBusy || linked.has(task.id)) return;
    setPendingId(task.id);
    const isLinked = await review.link(file.id, task);
    setPendingId(undefined);
    // The search stays open to link more; a press outside the popover closes it.
    if (isLinked) searchRef.current?.focus();
  };
  const loadMore = async () => {
    if (!query.data || !query.hasNextPage) return;
    const pageCount = query.data.pages.length;
    // A second click while the page loads would ask for the same page again.
    if (
      lastPageRequest.current?.search === search &&
      lastPageRequest.current.pageCount === pageCount
    )
      return;
    lastPageRequest.current = { search, pageCount };
    const result = await query.fetchNextPage({ cancelRefetch: false });
    if (result.isError) lastPageRequest.current = undefined;
  };

  return (
    <Column
      gap={1}
      onKeyDown={(event) => {
        // Escape steps out of the search, not out of the popover.
        if (event.key !== "Escape") return;
        event.stopPropagation();
        onClose();
      }}
    >
      <Box
        // Its magnifier lines up with the row icons below, as the rows' own inset does.
        px={1}
      >
        <SearchInput
          ref={searchRef}
          width="100%"
          aria-label="Search tasks"
          placeholder="Search tasks…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            const first = tasks[firstLinkable];
            if (event.key === "Enter" && first) void link(first);
            if (event.key === "ArrowDown") {
              event.preventDefault();
              firstRowRef.current?.focus();
            }
          }}
        />
      </Box>
      <Column
        ref={scrollFade.ref}
        sx={scrollFade.sx}
        maxHeight={56}
        overflowY="auto"
        // Row icons line up with the linked tasks below, inset 12px in the popover's body.
        px={1}
        aria-busy={query.isFetching}
        onKeyDown={moveFocusWithArrows}
      >
        {query.error ? (
          <EmptyState
            variant="minimal"
            title="Could not load tasks"
            message="Try again."
          />
        ) : query.isLoading ? (
          <ReviewPickerNote>Loading tasks…</ReviewPickerNote>
        ) : !tasks.length ? (
          <EmptyState
            variant="minimal"
            title={
              search.trim()
                ? `No tasks match "${search.trim()}"`
                : "No tasks yet"
            }
            message={
              search.trim()
                ? "Try a different search."
                : "Create a task to link it."
            }
          />
        ) : (
          tasks.map((task, index) => {
            const isLinked = linked.has(task.id);
            return (
              <ReviewPickerRow
                key={task.id}
                ref={index === firstLinkable ? firstRowRef : undefined}
                icon={
                  <StatusIcon
                    status={task.status}
                    category={task.status ? undefined : taskCategory(task)}
                  />
                }
                label={task.title}
                // Stacked like the linked tasks: the project sits under the title.
                description={task.project?.title}
                meta={isLinked ? "Linked" : undefined}
                isDisabled={isBusy || isLinked}
                isPending={pendingId === task.id}
                onClick={() => void link(task)}
              />
            );
          })
        )}
        {query.hasNextPage && (
          <Box pt={1}>
            <Button
              size="sm"
              variant="tertiary"
              isLoading={query.isFetchingNextPage}
              onClick={() => void loadMore()}
            >
              Load more tasks
            </Button>
          </Box>
        )}
      </Column>
    </Column>
  );
}
